import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { DocumentV3 } from '@docgen/schema';
import { ArrowLeftIcon, FileDownIcon, MonitorIcon, SaveIcon, SparklesIcon } from 'lucide-react';
import { Button, ErrorNotice, Tabs } from '@bcecloud/portal-ui';
import { cn } from '@/lib/utils';
import type { PricingResult, ProposalRecord, ReadinessIssue } from '../lib/api';
import { exportIssuesMessage } from '../editor/ExportIssuesNotice';
import { SAVE_STATE, type SaveState } from '../editor/types';
import { PhonePreview } from './PhonePreview';
import { QuickEdits } from './QuickEdits';

/** The phone builder's two views. */
export const PHONE_VIEW = { preview: 'preview', edit: 'edit' } as const;
export type PhoneView = (typeof PHONE_VIEW)[keyof typeof PHONE_VIEW];

const VIEW_TABS = [
  { key: PHONE_VIEW.preview, label: 'Preview' },
  { key: PHONE_VIEW.edit, label: 'Quick edits' },
] as const;

const SAVE_STATUS: Record<SaveState, string> = {
  [SAVE_STATE.saved]: 'All changes saved',
  [SAVE_STATE.saving]: 'Saving…',
  [SAVE_STATE.dirty]: 'Unsaved changes',
  [SAVE_STATE.error]: 'Save failed. Tap Save to try again.',
};

export interface PhoneBuilderProps {
  readonly proposalId: string;
  readonly doc: DocumentV3;
  /** The proposal's pricing and totals; null until it loads. */
  readonly meta: Omit<ProposalRecord, 'document'> | null;
  readonly saveState: SaveState;
  readonly onSave: () => void;
  readonly onChange: (next: DocumentV3, coalesceKey: string) => void;
  readonly onPricingResult: (result: PricingResult) => void;
  /** The print view the PDF opens from. */
  readonly exportHref: string;
  /** What stops the PDF (sample text, empty required fields); empty when it can go. */
  readonly exportIssues: readonly ReadinessIssue[];
  /** Opens the assistant; absent when the assistant is switched off. */
  readonly onAskAssistant?: (() => void) | undefined;
}

/**
 * The proposal builder on a phone. The block canvas needs a mouse and a wide
 * screen, so a phone gets what a rep does away from a desk instead: read the
 * proposal as the client will (Preview), change the deal facts and pricing
 * (Quick edits), then save, check the PDF, or ask the assistant from a bar
 * pinned to the bottom of the screen. Layout changes wait for a computer.
 */
export function PhoneBuilder({
  proposalId,
  doc,
  meta,
  saveState,
  onSave,
  onChange,
  onPricingResult,
  exportHref,
  exportIssues,
  onAskAssistant,
}: PhoneBuilderProps): React.ReactElement {
  const [view, setView] = useState<PhoneView>(PHONE_VIEW.preview);
  const [showBlocked, setShowBlocked] = useState(false);
  const blocked = exportIssues.length > 0;

  const openPdf = (): void => {
    if (blocked) setShowBlocked(true);
    else window.open(exportHref, '_blank', 'noopener');
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-portal-bg">
      <header className="shrink-0 border-b border-portal-line bg-white px-4 pt-2">
        <div className="flex items-center gap-2">
          <Link
            to="/proposals"
            aria-label="Back to proposals"
            className="-ml-2 grid size-10 shrink-0 place-items-center rounded-portal-button text-portal-muted hover:text-portal-ink"
          >
            <ArrowLeftIcon aria-hidden className="size-5" />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-portal-small font-bold">{doc.name}</h1>
            <p
              role="status"
              className={cn('text-portal-micro text-portal-muted', saveState === SAVE_STATE.error && 'text-rose-700')}
            >
              {SAVE_STATUS[saveState]}
            </p>
          </div>
        </div>
        {/* Tabs brings its own 20px bottom margin for a page; here the bar ends the header. */}
        <div className="-mb-5">
          <Tabs label="Proposal views" items={VIEW_TABS} active={view} onChange={setView} />
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {view === PHONE_VIEW.preview ? (
          <PhonePreview doc={doc} />
        ) : meta ? (
          <QuickEdits
            proposalId={proposalId}
            doc={doc}
            pricing={meta.pricing}
            totals={meta.totals}
            onChange={onChange}
            onPricingResult={onPricingResult}
          />
        ) : null}
        <p className="flex items-start gap-2 px-4 py-4 text-portal-micro text-portal-muted">
          <MonitorIcon aria-hidden className="mt-0.5 size-4 shrink-0" />
          To add, move or restyle pages and blocks, open this proposal in the full editor on a computer.
        </p>
      </div>

      <footer className="shrink-0 border-t border-portal-line bg-white px-4 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        {showBlocked && blocked ? (
          <div className="mb-2 max-h-[40dvh] overflow-y-auto">
            <ErrorNotice
              inline
              title="This proposal can't be exported yet."
              message={exportIssuesMessage(exportIssues)}
              action={
                <Button variant="secondary" size="compact" className="h-10" onClick={() => setShowBlocked(false)}>
                  Dismiss
                </Button>
              }
            />
          </div>
        ) : null}
        <div className={cn('grid gap-2', onAskAssistant ? 'grid-cols-3' : 'grid-cols-2')}>
          <Button
            variant="secondary"
            className="h-11 px-2"
            onClick={onSave}
            disabled={saveState === SAVE_STATE.saving}
          >
            <SaveIcon aria-hidden className="size-4" />
            Save
          </Button>
          <Button
            variant="secondary"
            className="h-11 px-2"
            onClick={openPdf}
          >
            <FileDownIcon aria-hidden className="size-4" />
            PDF
          </Button>
          {onAskAssistant ? (
            <Button variant="primary" className="h-11 px-2" onClick={onAskAssistant}>
              <SparklesIcon aria-hidden className="size-4" />
              Assistant
            </Button>
          ) : null}
        </div>
        {blocked && !showBlocked ? (
          <p className="mt-1 text-center text-portal-micro text-portal-muted">
            The PDF can't be made until a few things are fixed. Tap PDF to see them.
          </p>
        ) : null}
      </footer>
    </div>
  );
}
